import { Box } from '@mui/material';
import { ReactNode, useMemo, useRef } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { DefaultWaitingScreen } from '../ApiLoadingGuard/DefaultWaitingScreen';
import ActivityBar from './ActivityBar';
import ActivityOverview from './ActivityOverview';
import { useScrollToRecording } from './hooks/scrollToRecording';
import type { ActivityRecording } from './ActivityTile';

export interface LiveViewLayoutActivity {
  id: string;
  captureStartAt: string;
  renderTile: (isFocus: boolean) => ReactNode;
}

interface LiveViewLayoutProps {
  jobsiteId: string;
  isCameraOnline: boolean;
  activities: LiveViewLayoutActivity[];
  overviewRecordings: ActivityRecording[];
  licensePlates: Record<string, string>;
}

const ACTIVITY_TILE_SIZE = 850;

export function LiveViewLayout({
  jobsiteId,
  isCameraOnline,
  activities,
  overviewRecordings,
  licensePlates,
}: LiveViewLayoutProps) {
  const activityContainerRef = useRef<HTMLDivElement>(null);
  const activityListRef = useRef<FixedSizeList<ActivityRowData>>(null);

  const { activeRecordingId, scrollContainerRef, setRecordingRef, autoScrollToRecording, handleScroll } =
    useScrollToRecording({
      activities,
      activityTileSize: ACTIVITY_TILE_SIZE,
    });

  const latestCaptureStartAt = activities.length > 0 ? activities[0].captureStartAt : '';
  const itemData = useMemo<ActivityRowData>(() => {
    return {
      activities,
      setRecordingRef,
      activeRecordingId,
    };
  }, [activities, setRecordingRef, activeRecordingId]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <ActivityBar isCameraOnline={isCameraOnline} captureStartAt={latestCaptureStartAt} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '3fr 1fr',
          minHeight: 0,
          height: '100%',
        }}
        ref={activityContainerRef}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingRight: '0.5em',
            boxSizing: 'border-box',
            height: '100%',
          }}
        >
          {activities.length === 0 && <DefaultWaitingScreen text='activity.waitingForActivity' />}

          <FixedSizeList<ActivityRowData>
            ref={activityListRef}
            outerRef={scrollContainerRef}
            onScroll={handleScroll}
            height={activityContainerRef.current?.clientHeight || 0}
            itemCount={activities.length}
            itemSize={ACTIVITY_TILE_SIZE}
            width='100%'
            itemData={itemData}
            overscanCount={2}
          >
            {ActivityRow}
          </FixedSizeList>
        </Box>
        <ActivityOverview
          jobsiteId={jobsiteId}
          recordings={overviewRecordings}
          licensePlates={licensePlates}
          activeRecording={activeRecordingId}
          onSelectRecording={autoScrollToRecording}
        />
      </Box>
    </Box>
  );
}

interface ActivityRowData {
  activities: LiveViewLayoutActivity[];
  setRecordingRef: (id: string) => (el: HTMLDivElement | null) => void;
  activeRecordingId: string;
}

function ActivityRow({ data, index, style }: ListChildComponentProps<ActivityRowData>) {
  const activity = data.activities[index];

  return (
    <div
      data-section
      key={activity.id}
      ref={data.setRecordingRef(activity.id)}
      style={{ ...style, padding: '0.5em', boxSizing: 'border-box' }}
      id={activity.id}
    >
      {activity.renderTile(data.activeRecordingId === activity.id)}
    </div>
  );
}
